<script setup lang="ts">

import { FeatureFrame } from "../Components";
import type { IRoadsideAssistanceFeature } from "@domain/CarePlan";
import { useViewModelInstance } from "@app/MyGarage/Types/MyGarageViewModelStore";

const model = defineModel<IRoadsideAssistanceFeature>( { required: true } ); 
const viewModel = await useViewModelInstance();
const attributes = [
    { title: 'Battery', value: model.value.battery },
    { title: 'Tyres', value: model.value.tyres },
    { title: 'Fuel', value: model.value.fuel },
    { title: 'Lock Outs', value: model.value.lockout },
    { title: 'Towing', value: model.value.towing },
    { title: 'Car Hire', value: model.value.carHire },
    { title: 'Accomodation', value: model.value.accommodation },
    { title: 'Ambulance', value: model.value.ambulance },
    { title: 'General', value: model.value.general },
].filter( a => a.value != undefined && a.value.length > 0 );

</script>

<template>

    <FeatureFrame :featured="true">
        
        <template #title><h1 class="text-lg 2xl:text-2xl font-semibold">{{ model.title }}</h1></template>
        <template #sub-title>
            <div class="px-2 3xl:px-4 text-base text-center -mb-2">
                Gets you moving again if you're stuck at the side of the road
            </div>
        </template>

        <div class="flex flex-col w-full pt-4">
               
            <div class="flex flex-col">
                <div class="px-2 3xl:px-4 text-base text-center my-4 text-white/80">
                    A rewards program from your dealership to help you keep your car healthy and your wallet happy
                </div>
            </div>
                
            <!-- PROPERTIES -->
            <div class="flex flex-col w-full gap-1
                        text-xs lg:text-sm 3xl:text-base tracking-wider">

                <div v-if="model.provider" class="flex gap-2 3xl:py-1">
                    <div>Provider</div>
                    <div class="flex-1 border-b-2 border-dotted opacity-60" />
                    <div class="uppercase text-right">{{ model.provider }}</div>
                </div>  

                <div v-if="model.coverType" class="flex gap-2 3xl:py-1">
                    <div>Cover Type</div>
                    <div class="flex-1 border-b-2 border-dotted opacity-60" />
                    <div class="uppercase text-right">{{ model.coverType }}</div>
                </div>  

                <div v-for="attribute in attributes" class="flex gap-2 3xl:py-1 items-start">
                    <div>{{ attribute.title }}</div>
                    <div class="flex-1 border-b-2 border-dotted opacity-60 min-w-8 h-[1rem]" />
                    <div class="text-right text-balance">
                        {{ attribute.value }}
                    </div>
                </div>  

                <div class="grid grid-cols-3 mt-6">
                    <div class="col-span-3 opacity-60 text-xs 3xl:text-base text-center">
                        Refer to docs for full Terms &amp; Conditions
                    </div>
                </div> 

            </div>           
            
            <!-- ACTIONS -->
            <div class="w-full flex flex-col
                        gap-x-1 gap-y-3 md:gap-x-2 md:gap-y-4 3xl:gap-4 mt-4 3xl:mt-3">

                <RoadsideAssistanceButton 
                    v-if="viewModel.carePlan.roadsideAssistance.isEnabled"
                    class="mb-3" /> 

                <JupiterButton v-if="viewModel.carePlan.roadsideAssistance.isEnabled"  
                               variant="outlined"
                               color="white/50"
                               type="url" 
                               :to="model.linkUrl"
                               class="col-span-2 !h-10 lg:!h-12 3xl:!h-14">
                    <span class="flex items-center gap-1 text-white">
                        <Icon name="contract" class="text-2xl !hidden md:!block" />
                        <span class="3xl:text-base">
                            Documents
                        </span>
                    </span>
                </JupiterButton>

                <div class="-mb-2 -mt-2 flex justify-stretch w-full">
                    <Button variant="text"
                            to="/my-garage/concierge"
                            class="!h-10 lg:!h-12 3xl:!h-14 grow">
                        <span>
                            More Options
                        </span>
                    </Button>
                    
                    <Button variant="text"
                            to="/my-garage/dealership"
                            class="!h-10 lg:!h-12 3xl:!h-14 grow">
                        <span>
                            Contact Dealer
                        </span>
                    </Button>
                </div>
                
            </div>
        </div>
   </FeatureFrame>
        
</template>
